import { useMemo, useState } from 'react';
import { useEmployeeNameFormatter } from '@/lib/employee-name';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { formatDate, formatDateTime, toDateParam, parseDateOnly } from '@/lib/date';
import { usePermission } from '@/lib/permissions';
import { completeProbation, extendProbation, setProbation } from '@/lib/employees';
import { updateProjectMember } from '@/lib/projects';
import { employeesKeys } from '@/hooks/use-employees';
import type { EmployeeDetail, EmployeeProjectMembership } from '@/types/employee';
import { Link } from '@tanstack/react-router';
import { Briefcase, Building2, Calendar, CalendarClock, CalendarPlus, Check, ChevronRight, FolderKanban, Loader2, Pencil, Search, Users, X } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { EditCurrentPositionDialog } from './edit-current-position-dialog';
import { IconActionButton } from './icon-action-button';
import { EditManagerDialog } from './edit-manager-dialog';
import { CustomBlocksSection } from './custom-blocks/custom-blocks-section';
import { HistoryPreview } from './history-preview';

interface JobTabProps {
  employee: EmployeeDetail;
  /** `employees.audit.view` — gates the change-history column and its request. [HW-795] */
  canViewHistory?: boolean;
  /** Opens the History tab from the column's "Show all history". */
  onShowAllHistory?: () => void;
}

function getInitials(
  firstName: string | null | undefined,
  lastName: string | null | undefined
): string {
  const f = firstName?.charAt(0) || '';
  const l = lastName?.charAt(0) || '';
  return `${f}${l}`.toUpperCase() || '?';
}

function formatEmploymentType(type: string | null | undefined): string {
  if (!type) return '-';
  const labels: Record<string, string> = {
    full_time: 'Full-time',
    part_time: 'Part-time',
    contract: 'Contract',
    intern: 'Intern',
  };
  return labels[type] || type;
}

/**
 * "Jun 2026" for membership timestamps (joinedAt / removedAt, `@db.Timestamptz`),
 * rendered in the viewer's local timezone.
 */
function formatMembershipMonthYear(date: string | Date | null | undefined): string {
  return formatDateTime(date, { month: 'short', year: 'numeric' });
}

interface SectionProps {
  title: string;
  children: React.ReactNode;
  onEdit?: () => void;
}

function Section({ title, children, onEdit }: SectionProps) {
  return (
    <Card className="overflow-hidden">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 border-b border-border/50 bg-muted/30 px-5 py-3">
        <CardTitle className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          {title}
        </CardTitle>
        {onEdit && <IconActionButton icon={Pencil} tooltip="Edit" onClick={onEdit} />}
      </CardHeader>
      <CardContent className="p-5">{children}</CardContent>
    </Card>
  );
}

interface FieldProps {
  label: string;
  value: string | null | undefined;
  icon?: typeof Briefcase;
}

function Field({ label, value, icon: Icon }: FieldProps) {
  const displayValue = value || '-';

  return (
    <div className="flex items-start gap-2">
      {Icon && (
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-hygge/10 text-hygge mt-0.5">
          <Icon className="h-4 w-4" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">
          {label}
        </div>
        <div className="text-xs font-medium text-foreground">{displayValue}</div>
      </div>
    </div>
  );
}

export function JobTab({ employee, canViewHistory = false, onShowAllHistory }: JobTabProps) {
  const formatName = useEmployeeNameFormatter();
  const queryClient = useQueryClient();
  const canEdit = usePermission('employees.profile.edit');
  const [editCurrentPositionOpen, setEditCurrentPositionOpen] = useState(false);
  const [editManagerOpen, setEditManagerOpen] = useState(false);
  const [probationDialogOpen, setProbationDialogOpen] = useState(false);
  const [probationDialogMode, setProbationDialogMode] = useState<'set' | 'extend'>('set');
  const [newProbationDate, setNewProbationDate] = useState<Date | null>(null);

  const invalidateEmployee = () => {
    queryClient.invalidateQueries({ queryKey: employeesKeys.detail(employee.id) });
    queryClient.invalidateQueries({ queryKey: employeesKeys.lists() });
    queryClient.invalidateQueries({ queryKey: employeesKeys.stats() });
  };

  const openProbationDialog = (mode: 'set' | 'extend') => {
    setProbationDialogMode(mode);
    setNewProbationDate(
      mode === 'extend' && employee.probationEndDate
        ? parseDateOnly(employee.probationEndDate)
        : null,
    );
    setProbationDialogOpen(true);
  };

  const completeProbationMutation = useMutation({
    mutationFn: () => completeProbation(employee.id),
    onSuccess: invalidateEmployee,
  });

  const probationDateMutation = useMutation({
    mutationFn: (newDate: string) =>
      probationDialogMode === 'set'
        ? setProbation(employee.id, newDate)
        : extendProbation(employee.id, newDate),
    onSuccess: () => {
      invalidateEmployee();
      setProbationDialogOpen(false);
    },
  });

  const handleProbationDateSubmit = () => {
    if (!newProbationDate) return;
    probationDateMutation.mutate(toDateParam(newProbationDate)!);
  };

  return (
    <div className="space-y-4">
      {/* Current Position (left) and, for employees.audit.view holders, the
          newest changes (right). Without it the position takes the full width
          rather than leaving an empty half. [HW-795] */}
      <Section title={canViewHistory ? 'Position & History' : 'Position'}>
        <div
          className={cn(
            'grid grid-cols-1 gap-6',
            canViewHistory && 'lg:grid-cols-2 divide-x-0 lg:divide-x divide-border'
          )}
        >
          {/* Current Position - Left side */}
          <div className={cn('flex flex-col', canViewHistory && 'pr-0 lg:pr-6')}>
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Current Position
              </h4>
              <div className="flex items-center gap-1.5">
                {canEdit && employee.status !== 'probation' && employee.status !== 'terminated' && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-[11px] cursor-pointer gap-1.5 text-hygge border-hygge/30 hover:bg-hygge/10"
                    onClick={() => openProbationDialog('set')}
                  >
                    <CalendarClock className="h-3 w-3" />
                    Set Probation
                  </Button>
                )}
                {canEdit && (
                  <IconActionButton
                    icon={Pencil}
                    tooltip="Edit"
                    onClick={() => setEditCurrentPositionOpen(true)}
                  />
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Position" value={employee.position?.title} icon={Briefcase} />
              <Field label="Department" value={employee.department?.name} icon={Building2} />
              <Field label="Division" value={employee.division?.name} icon={Users} />
              <Field
                label="Employment Type"
                value={formatEmploymentType(employee.employmentTypeRelation?.name)}
                icon={Briefcase}
              />
              {employee.seniorityLevel?.name && (
                <Field label="Seniority Level" value={employee.seniorityLevel.name} icon={Users} />
              )}
              <Field label="Start Date" value={formatDate(employee.startDate, 'dayMonth') || '-'} icon={Calendar} />
            </div>

            {/* Probation Management Block */}
            {employee.status === 'probation' && employee.probationEndDate ? (
              <div className="mt-auto pt-5">
                <div className="rounded-xl border border-border bg-muted/30 dark:bg-muted/20 p-4 pl-5 border-l-4 border-l-amber-500/50 dark:border-l-amber-400/40">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-amber-500/10 dark:bg-amber-400/10">
                        <CalendarClock className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                      </div>
                      <div>
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                          Probation Period
                        </p>
                        <p className="text-sm font-medium text-foreground mt-0.5">
                          Ends {formatDate(employee.probationEndDate, 'dayMonth') || '-'}
                        </p>
                      </div>
                    </div>
                    {canEdit && (
                      <TooltipProvider delayDuration={200}>
                        <div className="flex items-center gap-2 sm:shrink-0">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="outline"
                                size="icon"
                                className="h-8 w-8 cursor-pointer"
                                onClick={() => openProbationDialog('extend')}
                                disabled={completeProbationMutation.isPending}
                              >
                                <CalendarPlus className="h-4 w-4" />
                                <span className="sr-only">Adjust probation</span>
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent side="bottom">Adjust probation</TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                size="icon"
                                className="h-8 w-8 cursor-pointer"
                                onClick={() => completeProbationMutation.mutate()}
                                disabled={completeProbationMutation.isPending}
                              >
                                {completeProbationMutation.isPending ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Check className="h-4 w-4" />
                                )}
                                <span className="sr-only">Complete probation</span>
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent side="bottom">Complete probation</TooltipContent>
                          </Tooltip>
                        </div>
                      </TooltipProvider>
                    )}
                  </div>
                </div>
              </div>
            ) : null}
          </div>

          {canViewHistory && (
            <div className="pl-0 lg:pl-6 pt-4 lg:pt-0">
              <HistoryPreview employeeId={employee.id} onShowAll={onShowAllHistory} />
            </div>
          )}
        </div>
      </Section>

      {/* Projects */}
      {employee.projectMemberships && employee.projectMemberships.length > 0 && (
        <ProjectsSection
          employeeId={employee.id}
          memberships={employee.projectMemberships}
        />
      )}

      {/* Reporting */}
      <Section title="Reporting" onEdit={canEdit ? () => setEditManagerOpen(true) : undefined}>
        <div className="grid grid-cols-2 gap-6">
          {/* Reports To */}
          <div>
            <div className="mb-2 text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">
              Reports To
            </div>
            {(() => {
              // Use managers array if available, otherwise fallback to manager for backward compatibility
              const managersToShow = employee.managers && employee.managers.length > 0
                ? employee.managers
                : employee.manager
                  ? [employee.manager]
                  : [];

              if (managersToShow.length === 0) {
                return (
                  <Card className="border-dashed">
                    <div className="p-3">
                      <div className="text-xs text-muted-foreground text-center">
                        No managers assigned
                      </div>
                    </div>
                  </Card>
                );
              }

              return managersToShow.map((manager, index) => (
                <Card key={manager.id || index}>
                  <div className="p-4">
                    <div className="flex items-start gap-3">
                      <Link
                        to="/employees/$id"
                        params={{ id: manager.id }}
                        className="hover:opacity-80 transition-opacity shrink-0"
                      >
                        <Avatar className="h-12 w-12">
                          {manager.avatarUrl && (
                            <AvatarImage src={manager.avatarUrl} />
                          )}
                          <AvatarFallback className="bg-primary/10 text-primary text-base font-medium">
                            {getInitials(manager.firstName, manager.lastName)}
                          </AvatarFallback>
                        </Avatar>
                      </Link>
                      <div className="flex-1 min-w-0">
                        <Link
                          to="/employees/$id"
                          params={{ id: manager.id }}
                          className="text-sm font-semibold text-foreground hover:text-primary hover:underline text-left block w-full mb-1"
                        >
                          {formatName({ firstName: manager.firstName, lastName: manager.lastName })}
                        </Link>
                        {manager.position?.title && (
                          <div className="text-xs text-muted-foreground mb-1">
                            {manager.position.title}
                          </div>
                        )}
                        {manager.department?.name && (
                          <div className="text-[11px] text-muted-foreground mb-2">
                            {manager.department.name}
                          </div>
                        )}
                        {manager.workEmail && (
                          <a
                            href={`mailto:${manager.workEmail}`}
                            onClick={(e) => e.stopPropagation()}
                            className="text-[11px] text-primary hover:underline cursor-pointer break-all"
                          >
                            {manager.workEmail}
                          </a>
                        )}
                      </div>
                      <Link
                        to="/employees/$id"
                        params={{ id: manager.id }}
                        className="hover:text-primary transition-colors shrink-0"
                      >
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      </Link>
                    </div>
                  </div>
                </Card>
              ));
            })()}
          </div>

          {/* Direct Reports */}
          <div>
            <div className="mb-2 text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">
              Direct Reports ({employee.directReports?.length || 0})
            </div>
            {employee.directReports && employee.directReports.length > 0 ? (
              <div className="space-y-2">
                <div className="flex flex-wrap gap-2">
                  {employee.directReports
                    .slice(0, 6)
                    .map((report: NonNullable<EmployeeDetail['directReports']>[0]) => (
                      <div
                        key={report.id}
                        className="flex items-center gap-2 rounded-lg border border-border/50 bg-muted/30 px-3 py-2"
                      >
                        <Link
                          to="/employees/$id"
                          params={{ id: report.id }}
                          className="hover:opacity-80 transition-opacity"
                        >
                          <Avatar className="h-6 w-6">
                            {report.avatarUrl && <AvatarImage src={report.avatarUrl} />}
                            <AvatarFallback className="bg-primary/10 text-primary text-[10px] font-medium">
                              {getInitials(report.firstName, report.lastName)}
                            </AvatarFallback>
                          </Avatar>
                        </Link>
                        <div className="text-left">
                          <Link
                            to="/employees/$id"
                            params={{ id: report.id }}
                            className="text-xs font-medium text-foreground hover:text-primary hover:underline text-left"
                          >
                            {formatName({ firstName: report.firstName, lastName: report.lastName })}
                          </Link>
                          {report.position?.title && (
                            <div className="text-[10px] text-muted-foreground">
                              {report.position.title}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                </div>
                {employee.directReports.length > 6 && (
                  <Popover>
                    <PopoverTrigger asChild>
                      <button className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline cursor-pointer">
                        View all {employee.directReports.length}
                        <ChevronRight className="h-3 w-3" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-80 p-0" align="start">
                      <div className="p-3 border-b border-border">
                        <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                          All Direct Reports ({employee.directReports.length})
                        </h4>
                      </div>
                      <div className="max-h-[400px] overflow-y-auto">
                        <div className="p-2 space-y-1">
                          {employee.directReports.map(
                            (report: NonNullable<EmployeeDetail['directReports']>[0]) => (
                              <div
                                key={report.id}
                                className="flex w-full items-center gap-3 rounded-lg border border-border/50 bg-muted/30 p-2.5"
                              >
                                <Link
                                  to="/employees/$id"
                                  params={{ id: report.id }}
                                  className="hover:opacity-80 transition-opacity"
                                >
                                  <Avatar className="h-8 w-8">
                                    {report.avatarUrl && <AvatarImage src={report.avatarUrl} />}
                                    <AvatarFallback className="bg-primary/10 text-primary text-xs font-medium">
                                      {getInitials(report.firstName, report.lastName)}
                                    </AvatarFallback>
                                  </Avatar>
                                </Link>
                                <div className="flex-1 min-w-0">
                                  <Link
                                    to="/employees/$id"
                                    params={{ id: report.id }}
                                    className="text-xs font-medium text-foreground hover:text-primary hover:underline text-left truncate block w-full"
                                  >
                                    {formatName({ firstName: report.firstName, lastName: report.lastName })}
                                  </Link>
                                  {report.position?.title && (
                                    <div className="text-[10px] text-muted-foreground truncate">
                                      {report.position.title}
                                    </div>
                                  )}
                                </div>
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                )}
              </div>
            ) : (
              <Card className="border-dashed p-3">
                <div className="text-sm text-muted-foreground text-center">No direct reports</div>
              </Card>
            )}
          </div>
        </div>
      </Section>

      {/* Edit Dialogs */}
      <EditCurrentPositionDialog
        open={editCurrentPositionOpen}
        onOpenChange={setEditCurrentPositionOpen}
        employee={employee}
      />
      <EditManagerDialog
        open={editManagerOpen}
        onOpenChange={setEditManagerOpen}
        employee={employee}
      />

      {/* Set / Adjust Probation Dialog */}
      <Dialog open={probationDialogOpen} onOpenChange={setProbationDialogOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>
              {probationDialogMode === 'set' ? 'Set Probation Period' : 'Adjust Probation'}
            </DialogTitle>
            <DialogDescription>
              {probationDialogMode === 'set'
                ? `Put ${employee.firstName} ${employee.lastName} on a probation period.`
                : `Set a new probation end date for ${employee.firstName} ${employee.lastName}.`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-muted-foreground">
                Probation end date
              </Label>
              <DatePicker
                value={newProbationDate}
                onChange={setNewProbationDate}
                disabled={probationDateMutation.isPending}
                placeholder="Select date"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setProbationDialogOpen(false)}
              disabled={probationDateMutation.isPending}
              className="cursor-pointer"
            >
              Cancel
            </Button>
            <Button
              onClick={handleProbationDateSubmit}
              disabled={!newProbationDate || probationDateMutation.isPending}
              className="cursor-pointer"
            >
              {probationDateMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {probationDialogMode === 'set' ? 'Set Probation' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CustomBlocksSection section="JOB" employeeId={employee.id} />
    </div>
  );
}

// ─── Project helpers ───────────────────────────────────────────────────────

function isFutureDate(date: string | Date | null | undefined): boolean {
  if (!date) return false;
  // Compare on a calendar-day basis. Date-only strings (@db.Date joinedAt/removedAt,
  // UTC midnight) go through parseDateOnly to avoid the new Date() UTC drift; picker
  // Dates are already at local midnight. A membership starting today is not "future".
  const d = typeof date === 'string' ? parseDateOnly(date) : new Date(date);
  d.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return d > today;
}

/**
 * Convert a (start, end) interval into months. End falls back to "now" when
 * null (i.e. ongoing). Returns 0 when start is missing or future.
 */
function intervalMonths(start: string | null | undefined, end: string | null | undefined): number {
  if (!start) return 0;
  const s = new Date(start);
  const e = end ? new Date(end) : new Date();
  if (isNaN(s.getTime()) || isNaN(e.getTime())) return 0;
  if (e < s) return 0;
  const months =
    (e.getFullYear() - s.getFullYear()) * 12 + (e.getMonth() - s.getMonth());
  return Math.max(0, months);
}

/** "3y 1mo", "11mo", "<1mo" */
function formatTenure(months: number): string {
  if (months < 1) return '<1mo';
  const y = Math.floor(months / 12);
  const m = months % 12;
  if (y === 0) return `${m}mo`;
  if (m === 0) return `${y}y`;
  return `${y}y ${m}mo`;
}

/**
 * "Apr 2023 — now" or "Sep 2021 — Feb 2024" for membership timestamps
 * (joinedAt / removedAt, `@db.Timestamptz`), rendered in the local timezone.
 */
function formatDateRange(
  start: string | null | undefined,
  end: string | null | undefined,
): string {
  const startLabel = start ? formatMembershipMonthYear(start) : 'N/A';
  if (!end) return `${startLabel} — now`;
  return `${startLabel} — ${formatMembershipMonthYear(end)}`;
}

/** "in 37 days" for future starts. */
function daysUntil(date: string | Date | null | undefined): number {
  if (!date) return 0;
  const d = new Date(date);
  if (isNaN(d.getTime())) return 0;
  return Math.ceil((d.getTime() - Date.now()) / (24 * 60 * 60 * 1000));
}

interface ProjectStats {
  activeCount: number;
  pastCount: number;
  futureCount: number;
  pmCount: number;
  icCount: number;
  avgActiveTenureMonths: number;
  pastTenureMonths: number;
  longest: EmployeeProjectMembership | null;
  newest: EmployeeProjectMembership | null;
  pastRangeStart: string | null;
  pastRangeEnd: string | null;
}

function computeProjectStats(all: EmployeeProjectMembership[]): ProjectStats {
  const active = all.filter((m) => !m.removedAt);
  const past = all.filter((m) => m.removedAt);
  const future = active.filter((m) => isFutureDate(m.joinedAt));
  const pm = active.filter((m) => m.isManager);

  const activeTenures = active
    .filter((m) => !isFutureDate(m.joinedAt))
    .map((m) => intervalMonths(m.joinedAt, null));
  const avgActiveTenureMonths = activeTenures.length
    ? Math.round(activeTenures.reduce((a, b) => a + b, 0) / activeTenures.length)
    : 0;

  const pastTenures = past.map((m) => intervalMonths(m.joinedAt, m.removedAt));
  const pastTenureMonths = pastTenures.length
    ? Math.round(pastTenures.reduce((a, b) => a + b, 0) / pastTenures.length)
    : 0;

  // "Longest" = active member with earliest joinedAt (highest tenure so far).
  const longest = [...active]
    .filter((m) => m.joinedAt && !isFutureDate(m.joinedAt))
    .sort((a, b) => +new Date(a.joinedAt!) - +new Date(b.joinedAt!))[0] ?? null;

  // "Newest" = either the soonest-starting future row, or the latest-joined active row.
  const newest =
    future.sort((a, b) => +new Date(a.joinedAt!) - +new Date(b.joinedAt!))[0] ??
    [...active]
      .filter((m) => m.joinedAt && !isFutureDate(m.joinedAt))
      .sort((a, b) => +new Date(b.joinedAt!) - +new Date(a.joinedAt!))[0] ??
    null;

  const pastWithDates = past.filter((m) => m.joinedAt && m.removedAt);
  const pastRangeStart = pastWithDates
    .map((m) => m.joinedAt!)
    .sort()[0] ?? null;
  const pastRangeEnd = pastWithDates
    .map((m) => m.removedAt!)
    .sort()
    .reverse()[0] ?? null;

  return {
    activeCount: active.length,
    pastCount: past.length,
    futureCount: future.length,
    pmCount: pm.length,
    icCount: Math.max(0, active.length - pm.length),
    avgActiveTenureMonths,
    pastTenureMonths,
    longest,
    newest,
    pastRangeStart,
    pastRangeEnd,
  };
}

// ─── ProjectsSection (B2 layout) ───────────────────────────────────────────

function ProjectsSection({
  employeeId,
  memberships,
}: {
  employeeId: string;
  memberships: EmployeeProjectMembership[];
}) {
  const allMemberships: EmployeeProjectMembership[] = memberships;

  const [query, setQuery] = useState('');

  // Split + sort: active first (most-recent joinedAt first), past after
  // (most-recently-ended first). Future-starts surface to the top of active.
  const active = useMemo(() => {
    return [...allMemberships]
      .filter((m) => !m.removedAt)
      .sort((a, b) => {
        const aFuture = isFutureDate(a.joinedAt);
        const bFuture = isFutureDate(b.joinedAt);
        if (aFuture !== bFuture) return aFuture ? -1 : 1;
        // Among non-future, sort by joinedAt asc (longest-running on top).
        const aTime = a.joinedAt ? +new Date(a.joinedAt) : 0;
        const bTime = b.joinedAt ? +new Date(b.joinedAt) : 0;
        return aTime - bTime;
      });
  }, [allMemberships]);

  const past = useMemo(() => {
    return [...allMemberships]
      .filter((m) => m.removedAt)
      .sort((a, b) => +new Date(b.removedAt!) - +new Date(a.removedAt!));
  }, [allMemberships]);

  const stats = useMemo(() => computeProjectStats(allMemberships), [allMemberships]);

  // Inline filter — only consider the active list here. Past stays full
  // (it's collapsed anyway, and filter scope is usually "what am I on now").
  const filteredActive = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return active;
    return active.filter((m) => {
      const haystack = [
        m.project.name,
        m.project.slug,
        m.role,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [active, query]);

  // Search input only worth showing when list is non-trivially long.
  const showSearch = active.length > 8;

  return (
    <Card className="overflow-hidden">
      {/* ── Header ─────────────────────────────────────────────── */}
      <CardHeader className="flex flex-row items-center justify-between space-y-0 border-b border-border/50 bg-muted/30 px-5 py-3">
        <div className="flex items-center gap-2">
          <FolderKanban className="h-4 w-4 text-hygge" />
          <CardTitle className="text-[14px] font-semibold">Projects</CardTitle>
          <span className="text-muted-foreground text-[11.5px]">·</span>
          <span className="text-[12px] text-muted-foreground tabular-nums">
            <span className="text-foreground font-medium">{stats.activeCount}</span> active
            {' · '}
            <span className="text-foreground font-medium">{stats.pastCount}</span> past
            {' · '}
            <span className="text-foreground font-medium">{stats.pmCount}</span> as manager
          </span>
        </div>
      </CardHeader>

      {/* ── Two-column body: list (left) + stats rail (right) ── */}
      <div className="grid grid-cols-[1fr_280px] gap-0">

        {/* LEFT: scrollable active list + collapsible past */}
        <div className="border-r border-border/50 min-w-0">

          {/* Active sub-header with optional inline search */}
          <div className="px-5 py-2 border-b border-border/50 bg-muted/15 flex items-center gap-3">
            <div className="flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-hygge" />
              <h3 className="text-[11px] font-semibold uppercase tracking-wider">Active</h3>
              <span className="text-[11px] text-muted-foreground tabular-nums">
                {filteredActive.length}
                {query && filteredActive.length !== active.length && (
                  <span className="text-muted-foreground/70"> / {active.length}</span>
                )}
              </span>
            </div>
            {showSearch && (
              <div className="ml-auto relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground pointer-events-none" />
                <Input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Filter projects…"
                  className="h-6 pl-6 pr-2 text-[11.5px] w-44"
                />
              </div>
            )}
          </div>

          {/* Column header */}
          <div
            className="px-5 py-1.5 text-[10.5px] uppercase tracking-wider text-muted-foreground border-b border-border/40 grid items-center gap-3"
            style={{ gridTemplateColumns: '22px minmax(0,1fr) 140px auto' }}
          >
            <span />
            <span>Project & role</span>
            <span>Duration</span>
            <span />
          </div>

          {/* Scrollable active list (≈ 8 rows tall) */}
          {filteredActive.length === 0 ? (
            <div className="px-5 py-8 text-center text-xs text-muted-foreground">
              {query
                ? `No active projects match "${query}".`
                : 'Not assigned to any project.'}
            </div>
          ) : (
            <div
              className="overflow-y-auto"
              style={{
                maxHeight: '332px',
                maskImage:
                  'linear-gradient(to bottom, #000 0, #000 calc(100% - 24px), transparent 100%)',
                WebkitMaskImage:
                  'linear-gradient(to bottom, #000 0, #000 calc(100% - 24px), transparent 100%)',
              }}
            >
              <ul>
                {filteredActive.map((m) => (
                  <ProjectRow
                    key={m.id}
                    employeeId={employeeId}
                    membership={m}
                    isPast={false}
                  />
                ))}
              </ul>
            </div>
          )}

          {/* Past projects — native <details>, collapsed by default */}
          {past.length > 0 && (
            <details className="border-t-2 border-border/40 group">
              <summary className="px-5 py-2.5 bg-muted/15 hover:bg-muted/30 flex items-center gap-2.5 transition-colors text-[11px] uppercase tracking-wider cursor-pointer list-none [&::-webkit-details-marker]:hidden">
                <ChevronRight className="h-3 w-3 text-muted-foreground transition-transform group-open:rotate-90" />
                <span className="h-1.5 w-1.5 rounded-full bg-border" />
                <span className="font-semibold">Past projects</span>
                <span className="text-muted-foreground tabular-nums normal-case tracking-normal text-[11.5px]">
                  {stats.pastCount}
                </span>
                {stats.pastRangeStart && stats.pastRangeEnd && (
                  <span className="ml-auto text-muted-foreground normal-case tracking-normal text-[11px]">
                    {formatMembershipMonthYear(stats.pastRangeStart)} — {formatMembershipMonthYear(stats.pastRangeEnd)}
                  </span>
                )}
              </summary>
              <ul>
                {past.map((m) => (
                  <ProjectRow
                    key={m.id}
                    employeeId={employeeId}
                    membership={m}
                    isPast
                  />
                ))}
              </ul>
            </details>
          )}

          {/* Footer summary */}
          <div className="border-t border-border/50 bg-muted/20 px-5 py-2 flex items-center justify-between text-[11.5px] text-muted-foreground">
            <span>
              {allMemberships.length} total assignment{allMemberships.length === 1 ? '' : 's'}
            </span>
            {stats.avgActiveTenureMonths > 0 && (
              <span className="tabular-nums">
                avg active tenure{' '}
                <span className="text-foreground font-medium">
                  {formatTenure(stats.avgActiveTenureMonths)}
                </span>
              </span>
            )}
          </div>
        </div>

        {/* RIGHT: stats rail */}
        <aside className="bg-muted/15 p-5 space-y-5">
          {/* Currently on */}
          <div>
            <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground">
              Currently on
            </div>
            <div className="text-[28px] font-semibold text-hygge mt-0.5 tabular-nums">
              {stats.activeCount}{' '}
              <span className="text-muted-foreground font-normal text-[14px]">
                project{stats.activeCount === 1 ? '' : 's'}
              </span>
            </div>
            {stats.activeCount > 0 && (
              <>
                <div className="mt-2 h-1.5 rounded-full overflow-hidden bg-muted">
                  <div
                    className="h-full bg-hygge"
                    style={{
                      width: `${Math.round((stats.pmCount / stats.activeCount) * 100)}%`,
                    }}
                  />
                </div>
                <div className="mt-1 flex justify-between text-[10.5px] text-muted-foreground tabular-nums">
                  <span>{stats.pmCount} as PM</span>
                  <span>{stats.icCount} as IC</span>
                </div>
              </>
            )}
          </div>

          {/* Past projects */}
          <div className="border-t border-border/40 pt-4">
            <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground">
              Past projects
            </div>
            {stats.pastCount === 0 ? (
              <>
                <div className="text-[14px] font-medium mt-0.5 text-muted-foreground">
                  None yet
                </div>
                <p className="text-[11px] text-muted-foreground/80 mt-0.5">
                  When they roll off, projects will land here.
                </p>
              </>
            ) : (
              <>
                <div className="text-[18px] font-medium mt-0.5 tabular-nums">
                  {stats.pastCount}{' '}
                  <span className="text-muted-foreground font-normal text-[12px]">
                    closed
                  </span>
                </div>
                {stats.pastRangeStart && stats.pastRangeEnd && (
                  <p className="text-[11px] text-muted-foreground/80 mt-0.5 tabular-nums">
                    {formatMembershipMonthYear(stats.pastRangeStart)} → {formatMembershipMonthYear(stats.pastRangeEnd)}
                    {stats.pastTenureMonths > 0 &&
                      ` · avg ${formatTenure(stats.pastTenureMonths)} each`}
                  </p>
                )}
              </>
            )}
          </div>

          {/* Average active tenure */}
          {stats.avgActiveTenureMonths > 0 && (
            <div className="border-t border-border/40 pt-4">
              <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground">
                Average active tenure
              </div>
              <div className="text-[15px] font-medium mt-0.5">
                {formatTenure(stats.avgActiveTenureMonths)}
              </div>
            </div>
          )}

          {/* Longest running */}
          {stats.longest && stats.longest.joinedAt && (
            <div className="border-t border-border/40 pt-4">
              <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground">
                Longest running
              </div>
              <Link
                to="/projects/$id"
                params={{ id: stats.longest.project.id }}
                className="text-[13px] font-medium text-hygge hover:underline mt-0.5 inline-block"
              >
                {stats.longest.project.name}
              </Link>
              <div className="text-[11px] text-muted-foreground tabular-nums mt-0.5">
                {formatTenure(intervalMonths(stats.longest.joinedAt, null))} · since{' '}
                {formatMembershipMonthYear(stats.longest.joinedAt)}
              </div>
            </div>
          )}

          {/* Most recent / future */}
          {stats.newest && stats.newest.id !== stats.longest?.id && stats.newest.joinedAt && (
            <div className="border-t border-border/40 pt-4">
              <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground">
                {isFutureDate(stats.newest.joinedAt) ? 'Starting next' : 'Most recent'}
              </div>
              <Link
                to="/projects/$id"
                params={{ id: stats.newest.project.id }}
                className="text-[13px] font-medium text-hygge hover:underline mt-0.5 inline-block"
              >
                {stats.newest.project.name}
              </Link>
              {isFutureDate(stats.newest.joinedAt) ? (
                <div className="text-[11px] text-amber-600 dark:text-amber-400 tabular-nums mt-0.5">
                  in {daysUntil(stats.newest.joinedAt)} day
                  {daysUntil(stats.newest.joinedAt) === 1 ? '' : 's'}
                </div>
              ) : (
                <div className="text-[11px] text-muted-foreground tabular-nums mt-0.5">
                  since {formatMembershipMonthYear(stats.newest.joinedAt)}
                </div>
              )}
            </div>
          )}
        </aside>
      </div>
    </Card>
  );
}

function ProjectRow({
  employeeId,
  membership,
  isPast,
}: {
  employeeId: string;
  membership: EmployeeProjectMembership;
  isPast: boolean;
}) {
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [startDate, setStartDate] = useState<Date | undefined>(
    membership.joinedAt ? parseDateOnly(membership.joinedAt) : undefined,
  );
  const [endDate, setEndDate] = useState<Date | undefined>(
    membership.removedAt ? parseDateOnly(membership.removedAt) : undefined,
  );

  const isFutureStart = isFutureDate(membership.joinedAt);
  const isManagerOnly = membership.id.startsWith('managed-');
  const isEditingFutureStart = startDate ? isFutureDate(startDate) : false;

  const updateMutation = useMutation({
    mutationFn: (data: { joinedAt?: string; removedAt?: string | null }) =>
      updateProjectMember(membership.project.id, employeeId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: employeesKeys.detail(employeeId) });
      setIsEditing(false);
    },
  });

  const handleSave = () => {
    updateMutation.mutate({
      joinedAt: toDateParam(startDate) ?? undefined,
      removedAt: toDateParam(endDate),
    });
  };

  const handleCancel = () => {
    setIsEditing(false);
    setStartDate(membership.joinedAt ? parseDateOnly(membership.joinedAt) : undefined);
    setEndDate(membership.removedAt ? parseDateOnly(membership.removedAt) : undefined);
  };

  // Duration label per state. For future-start: "Starts <date>". For active:
  // "<tenure> · since <date>". For past: "<range> · <tenure>".
  const durationLabel = (() => {
    if (isFutureStart && membership.joinedAt) {
      return `Starts ${formatMembershipMonthYear(membership.joinedAt)}`;
    }
    if (isPast && membership.removedAt) {
      const months = intervalMonths(membership.joinedAt, membership.removedAt);
      const range = formatDateRange(membership.joinedAt, membership.removedAt);
      return months > 0 ? `${range} · ${formatTenure(months)}` : range;
    }
    if (membership.joinedAt) {
      const months = intervalMonths(membership.joinedAt, null);
      return months > 0
        ? `${formatTenure(months)} · since ${formatMembershipMonthYear(membership.joinedAt)}`
        : `since ${formatMembershipMonthYear(membership.joinedAt)}`;
    }
    return isManagerOnly ? 'Manager assignment' : 'No dates set';
  })();

  const rowBgClass = (() => {
    if (isFutureStart) return 'bg-amber-50/30 dark:bg-amber-500/5';
    return '';
  })();

  return (
    <li
      className={cn(
        'group px-5 py-2 border-b border-border/40 transition-colors hover:bg-muted/30',
        rowBgClass,
      )}
    >
      <div
        className="grid items-center gap-3"
        style={{ gridTemplateColumns: '22px minmax(0,1fr) 140px auto' }}
      >
        {/* Icon */}
        <div
          className={cn(
            'h-5 w-5 rounded grid place-items-center shrink-0',
            isFutureStart
              ? 'bg-amber-100 dark:bg-amber-500/20'
              : isPast
                ? 'bg-muted'
                : 'bg-hygge/10',
          )}
        >
          <FolderKanban
            className={cn(
              'h-3 w-3',
              isFutureStart
                ? 'text-amber-700 dark:text-amber-400'
                : isPast
                  ? 'text-muted-foreground'
                  : 'text-hygge',
            )}
          />
        </div>

        {/* Project name + role (stacked, compact) */}
        <div className="min-w-0">
          <Link
            to="/projects/$id"
            params={{ id: membership.project.id }}
            className={cn(
              'text-[13px] font-medium hover:underline truncate block',
              isPast ? 'text-hygge/80' : 'text-hygge',
            )}
          >
            {membership.project.name}
          </Link>
          {membership.role && (
            <div className="text-[11px] text-muted-foreground truncate">{membership.role}</div>
          )}
        </div>

        {/* Duration label */}
        <div
          className={cn(
            'text-[11.5px] tabular-nums truncate',
            isFutureStart
              ? 'text-amber-700 dark:text-amber-400 font-medium'
              : 'text-muted-foreground',
          )}
        >
          {durationLabel}
        </div>

        {/* Status pill + hover actions */}
        <div className="flex items-center gap-1.5">
          {isFutureStart ? (
            <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[10px] font-medium es-probation">
              <span className="h-1.5 w-1.5 rounded-full es-dot-probation" />
              Future
            </span>
          ) : isPast ? (
            <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[10px] font-medium bg-muted text-muted-foreground border border-border/60">
              <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/60" />
              Ended
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[10px] font-medium es-active">
              <span className="h-1.5 w-1.5 rounded-full es-dot-active" />
              Active
            </span>
          )}
          {!isManagerOnly && !isEditing && (
            <IconActionButton
              icon={Pencil}
              tooltip="Edit dates"
              onClick={() => setIsEditing(true)}
              size="sm"
              className="opacity-0 group-hover:opacity-100 transition-opacity"
            />
          )}
        </div>
      </div>

      {/* Inline edit panel (pushes content down only when active) */}
      {isEditing && (
        <div className="mt-2 ml-8 grid gap-2 rounded-md border border-border/60 bg-muted/20 p-2.5">
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-medium text-muted-foreground w-10 shrink-0">
              Start
            </span>
            <DatePicker
              value={startDate}
              onChange={(d) => {
                setStartDate(d ?? undefined);
                if (d && isFutureDate(d)) {
                  setEndDate(undefined);
                } else if (d && endDate && endDate < d) {
                  setEndDate(undefined);
                }
              }}
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-medium text-muted-foreground w-10 shrink-0">
              End
            </span>
            {isEditingFutureStart ? (
              <div className="flex items-center gap-2 h-9">
                <span className="text-xs font-medium text-hygge">Present</span>
                <span className="text-[10px] text-muted-foreground italic">
                  available after start
                </span>
              </div>
            ) : endDate !== undefined ? (
              <>
                <DatePicker
                  value={endDate}
                  onChange={(d) => setEndDate(d ?? undefined)}
                  minDate={startDate}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-[11px] text-hygge hover:text-hygge shrink-0 cursor-pointer"
                  onClick={() => setEndDate(undefined)}
                >
                  Set Present
                </Button>
              </>
            ) : (
              <div className="flex items-center gap-2 h-9">
                <span className="text-xs font-medium text-hygge">Present</span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-[11px] text-muted-foreground hover:text-foreground shrink-0 cursor-pointer"
                  onClick={() => setEndDate(new Date())}
                >
                  Set End Date
                </Button>
              </div>
            )}
          </div>
          <div className="flex items-center gap-1 pt-0.5">
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-[11px] text-primary cursor-pointer"
              onClick={handleSave}
              disabled={updateMutation.isPending}
            >
              {updateMutation.isPending ? (
                <Loader2 className="h-3 w-3 animate-spin mr-1" />
              ) : (
                <Check className="h-3 w-3 mr-1" />
              )}
              Save
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-[11px] text-muted-foreground cursor-pointer"
              onClick={handleCancel}
              disabled={updateMutation.isPending}
            >
              <X className="h-3 w-3 mr-1" />
              Cancel
            </Button>
          </div>
        </div>
      )}
    </li>
  );
}
